import React from 'react';
import { Container, Typography, Box, Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

const EngagingThumbnails: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  return (
    <Box sx={{ minHeight: '100vh' }}>
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/blog')}
          sx={{ mb: 4, color: 'text.secondary' }}
        >
          {t('blog.backToBlog')}
        </Button>

        <Typography variant="overline" color="primary" sx={{ fontWeight: 600, letterSpacing: 2, display: 'block', mb: 2 }}>
          Creator Tips • August 2, 2026
        </Typography>

        <Typography variant="h3" component="h1" sx={{ mb: 3, fontWeight: 700 }}>
          Creating Engaging Thumbnails: A Complete Guide
        </Typography>

        <Typography variant="body1" color="text.secondary" sx={{ mb: 4, lineHeight: 1.85, fontSize: '1.1rem' }}>
          Your thumbnail is your video's first impression—and often its only chance to grab attention. On IndeeClips, where thousands of videos compete for clicks every minute, a compelling thumbnail can be the difference between 100 views and 10,000 views. This comprehensive guide will teach you the art and science of creating thumbnails that get clicks.
        </Typography>

        <Typography variant="h5" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          Why Thumbnails Matter More Than You Think
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          Studies show that 90% of the best-performing videos on platforms like IndeeClips have custom thumbnails. Your thumbnail works together with your title to create what marketers call the "click decision." Even if you have the best content in the world, nobody will watch it if your thumbnail doesn't compel them to click.
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3, lineHeight: 1.85 }}>
          On mobile devices—where most IndeeClips viewing happens—thumbnails are even more critical because they're smaller and users scroll faster. You have literally one second to catch someone's eye and communicate what your video is about.
        </Typography>

        <Typography variant="h5" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          The Core Principles of Great Thumbnails
        </Typography>

        <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
          1. Contrast is King
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          Your thumbnail needs to pop against IndeeClips' white or dark background. Use high-contrast colors that make elements stand out:
        </Typography>
        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li><strong>Bright vs. dark backgrounds:</strong> If your subject is light, use a dark background and vice versa</li>
          <li><strong>Complementary colors:</strong> Blue + orange, purple + yellow, red + cyan create eye-catching combinations</li>
          <li><strong>Avoid muddy midtones:</strong> Don't be afraid of bold, saturated colors—subtle doesn't work at thumbnail size</li>
          <li><strong>Test at small size:</strong> View your thumbnail at actual size (120x90px on mobile) to ensure contrast holds up</li>
        </Box>

        <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
          2. Faces Are Powerful
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          Human brains are wired to notice faces. Thumbnails with expressive human faces consistently outperform those without:
        </Typography>
        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li><strong>Show emotion:</strong> Surprised, excited, shocked, or curious expressions trigger curiosity</li>
          <li><strong>Make eye contact:</strong> Looking directly at the camera creates connection with viewers</li>
          <li><strong>Close-ups work better:</strong> Your face should fill 40-60% of the thumbnail for maximum impact</li>
          <li><strong>Exaggerate expressions:</strong> What feels over-the-top to you looks engaging at thumbnail size</li>
          <li><strong>Be consistent:</strong> If you appear in your thumbnails, audiences start recognizing you in their feed</li>
        </Box>

        <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
          3. Text Should Enhance, Not Overwhelm
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          Text in your thumbnail should complement your title, not repeat it. Keep it punchy and minimal:
        </Typography>
        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li><strong>Maximum 3-5 words:</strong> "DON'T DO THIS" or "10X FASTER" work better than full sentences</li>
          <li><strong>Use bold, readable fonts:</strong> Impact, Anton, Bebas Neue, or Montserrat Heavy are YouTube-creator favorites</li>
          <li><strong>Add text outlines:</strong> White text with black outline (or vice versa) ensures readability on any background</li>
          <li><strong>Size matters:</strong> Text should be readable even when thumbnail is shrunk to mobile size</li>
          <li><strong>Strategic placement:</strong> Don't cover your face or key visual elements with text</li>
        </Box>

        <Typography variant="h5" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          The Anatomy of a Click-Worthy Thumbnail
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          Let's break down what makes a thumbnail work using real-world principles that top creators use:
        </Typography>

        <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
          Rule of Thirds Composition
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          Divide your thumbnail into a 3x3 grid. Place key elements along the grid lines or at intersection points. This creates visual balance that feels naturally pleasing to the eye. For example: your face on the left third, text on the right third, background element centered.
        </Typography>

        <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
          Create Visual Curiosity Gaps
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          Your thumbnail should raise a question that only watching the video can answer. Successful techniques include:
        </Typography>
        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li><strong>Before/after splits:</strong> Show transformation but leave the "how" unexplained</li>
          <li><strong>Arrows and circles:</strong> Point to something interesting in the frame to direct attention</li>
          <li><strong>Reaction shots:</strong> Your shocked expression makes viewers wonder "what did they see?"</li>
          <li><strong>Partial reveals:</strong> Show half the result, cutting off just enough to create intrigue</li>
        </Box>

        <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
          Consistent Branding Elements
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          While each thumbnail should be unique, maintaining some consistent elements helps build brand recognition:
        </Typography>
        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li><strong>Color palette:</strong> Use the same 2-3 signature colors across your content</li>
          <li><strong>Font style:</strong> Stick to one or two fonts that become associated with your channel</li>
          <li><strong>Logo placement:</strong> A small, consistent logo in the corner builds brand awareness</li>
          <li><strong>Layout template:</strong> Having a recognizable layout helps your thumbnails stand out in feeds</li>
        </Box>

        <Typography variant="h5" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          Tools for Creating Thumbnails
        </Typography>

        <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
          Free Tools
        </Typography>
        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li><strong>Canva (Web/Mobile):</strong> Best for beginners. Drag-and-drop interface, thousands of templates, free stock photos. Free tier is excellent; Pro (₹500/month) adds custom fonts and background remover.</li>
          <li><strong>Photopea (Web):</strong> Free Photoshop alternative that runs in your browser. Powerful if you know basic photo editing. Zero learning curve if you've used Photoshop before.</li>
          <li><strong>GIMP (Desktop):</strong> Free, open-source photo editor. More complex than Canva but infinitely customizable. Best for creators who want professional control without Adobe pricing.</li>
          <li><strong>Pixlr (Web/Mobile):</strong> Quick and simple editor for straightforward thumbnail needs. Good for adding text overlays and basic adjustments.</li>
        </Box>

        <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
          Paid Tools (Worth It for Serious Creators)
        </Typography>
        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li><strong>Adobe Photoshop (₹1,675/month):</strong> Industry standard. Unlimited creative possibilities, but overkill for most creators unless you're already using Creative Cloud.</li>
          <li><strong>Affinity Photo (₹4,400 one-time):</strong> Photoshop-level features without subscription. Excellent value if you want professional tools permanently.</li>
          <li><strong>Snappa (₹10/month):</strong> Built specifically for social media graphics. Templates optimized for YouTube thumbnails, drag-and-drop simplicity.</li>
        </Box>

        <Typography variant="h5" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          Common Thumbnail Mistakes to Avoid
        </Typography>
        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li><strong>Using auto-generated thumbnails:</strong> Never rely on IndeeClips' auto-selected frame. Always upload custom thumbnails.</li>
          <li><strong>Too much text:</strong> If your thumbnail has more than 6 words, cut it down. Text should create intrigue, not explain the entire video.</li>
          <li><strong>Low resolution:</strong> Always design at 1280x720 (16:9 ratio) and export at high quality. Blurry thumbnails kill credibility.</li>
          <li><strong>Clickbait that disappoints:</strong> Your thumbnail should represent your content. Misleading thumbnails get clicks but lose subscribers.</li>
          <li><strong>Ignoring mobile view:</strong> 70% of IndeeClips views come from mobile. Test how your thumbnail looks at phone size.</li>
          <li><strong>Copying other creators exactly:</strong> Learning from successful thumbnails is smart. Plagiarizing them destroys your unique brand.</li>
          <li><strong>Cluttered composition:</strong> One clear focal point beats five competing elements. Simplify ruthlessly.</li>
        </Box>

        <Typography variant="h5" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          A/B Testing Your Thumbnails
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          Even professional designers can't predict what will resonate with your specific audience. The solution? Test different approaches and let data guide you:
        </Typography>
        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li><strong>Create 2-3 variations:</strong> Try different expressions, text, colors, or compositions</li>
          <li><strong>Change thumbnails early:</strong> If a video underperforms in its first 24-48 hours, swap the thumbnail and see if CTR improves</li>
          <li><strong>Document what works:</strong> Keep a swipe file of your best-performing thumbnails to identify patterns</li>
          <li><strong>Survey your audience:</strong> Ask in comments or community posts which thumbnail style they prefer</li>
        </Box>

        <Typography variant="h5" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          Quick Thumbnail Checklist
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          Before uploading your next video, verify your thumbnail passes these tests:
        </Typography>
        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li>✓ High contrast—stands out against light and dark backgrounds</li>
          <li>✓ Readable at mobile size—text is legible even when small</li>
          <li>✓ Creates curiosity—makes viewers want to know more</li>
          <li>✓ Accurately represents content—isn't misleading clickbait</li>
          <li>✓ Includes human element—preferably an expressive face</li>
          <li>✓ Has minimal text—3-5 words maximum</li>
          <li>✓ Resolution is 1280x720—looks crisp and professional</li>
          <li>✓ Matches your brand—consistent with your other thumbnails</li>
        </Box>

        <Box
          sx={{
            mt: 6,
            p: 4,
            bgcolor: 'background.paper',
            borderRadius: 2,
            border: 1,
            borderColor: 'divider',
          }}
        >
          <Typography variant="h5" sx={{ mb: 2, fontWeight: 700 }}>
            Start Creating Thumbnails That Convert
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3, lineHeight: 1.85 }}>
            Great thumbnails are a skill, not a talent. Every creator you admire started with terrible thumbnails and improved through iteration and testing. Don't aim for perfection on your first try—aim for progress. Create your thumbnail, publish your video, analyze the results, and improve next time.
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.85 }}>
            The thumbnail formula that works for one creator might fail for another because audiences are different. Find your style, test relentlessly, and double down on what works for YOUR viewers. Need feedback on your thumbnail strategy? Email us at{' '}
            <Typography
              component="a"
              href="mailto:contact@indeeclips.com"
              sx={{ color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
            >
              contact@indeeclips.com
            </Typography>
            .
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default EngagingThumbnails;
